import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Dialog, DialogActions, DialogContent, IconButton, InputAdornment, MenuItem, Pagination, Skeleton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { MdChat, MdClose, MdPersonOff, MdPriorityHigh, MdRefresh, MdSearch, MdVisibility } from 'react-icons/md';
import { FaRegCircle, FaRegCheckCircle, FaSpinner } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import { getData, postData } from '../../Webservice/Webservice';
import { FONT, PriorityChip, StatusChip, ago, authToken, dt, initial, yellowBtn } from './ticketShared';

const LIMIT = 15;
const EMPTY = { status: '', priority: '', assigned: '', hasOrder: '', from: '', to: '' };
const head = { fontFamily: FONT, fontWeight: 800, fontSize: 12.5, color: '#475569', background: '#f8fafc', whiteSpace: 'nowrap' };
const cell = { fontFamily: FONT, fontSize: 13.5 };

const Kpi = ({ title, value, icon: Icon, from, to, active, onClick }) => (
  <Box onClick={onClick} sx={{ flex: 1, minWidth: 160, p: 2, borderRadius: '16px', color: '#fff', background: `linear-gradient(135deg,${from},${to})`, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', outline: active ? '3px solid #0f172a' : 'none', outlineOffset: 2 }}>
    <Box sx={{ background: 'rgba(255,255,255,.22)', borderRadius: '12px', p: 1.1, display: 'flex' }}><Icon size={20} /></Box>
    <Box>
      <Typography sx={{ fontFamily: FONT, fontSize: 12.5, opacity: 0.9, fontWeight: 700 }}>{title}</Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: 24, fontWeight: 800, lineHeight: 1.1 }}>{value}</Typography>
    </Box>
  </Box>
);

const Card = ({ title, accent, children }) => (
  <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderTop: `4px solid ${accent}`, borderRadius: '14px', p: 2.25 }}>
    <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, mb: 1 }}>{title}</Typography>
    {children}
  </Box>
);
const Row = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1, borderBottom: '1px dashed #e2e8f0' }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{label}</Typography>
    <Typography component="div" sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, textAlign: 'right', wordBreak: 'break-word' }}>{value || '—'}</Typography>
  </Box>
);

const Queries = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [stats, setStats] = useState({ total: 0, open: 0, inProgress: 0, resolved: 0, unassigned: 0, highPriority: 0 });
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const location = useLocation();
  // the dashboard links here with ?status=open|in-progress|active|resolved
  const urlStatus = new URLSearchParams(location.search).get('status');
  const [filters, setFilters] = useState({ ...EMPTY, status: ['open', 'in-progress', 'active', 'resolved'].includes(urlStatus) ? urlStatus : 'open' });
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState('');
  const seq = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: LIMIT, search: debounced, ...filters };
      Object.keys(params).forEach((k) => params[k] === '' && delete params[k]);
      const res = await getData('query/list', params, authToken(), false);
      if (mine !== seq.current) return;
      setRows(res?.queries || []);
      setMeta({ total: res?.meta?.total || 0, totalPages: res?.meta?.totalPages || 1 });
      if (res?.stats) setStats(res.stats);
      if (res?.staff) setStaff(res.staff);
    } catch {
      if (mine === seq.current) setError('Could not load support tickets.');
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [page, debounced, filters]);
  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };
  const pickStatus = (s) => setF('status', filters.status === s ? '' : s);
  const dirty = !!search || Object.entries(filters).some(([k, v]) => v && !(k === 'status' && v === 'open'));

  // quick changes from the popup
  const change = async (body) => {
    if (!selected) return;
    setSaving(true);
    setActionError('');
    try {
      const res = await postData(`query/update/${selected._id}`, body, authToken(), false);
      if (res?.query) setSelected((cur) => ({ ...cur, ...res.query, customerId: cur.customerId, orderId: cur.orderId }));
      setNotice('Ticket updated.');
      load();
    } catch (err) {
      setActionError(err?.message || 'Could not update the ticket.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>Support tickets</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b' }}>Questions and problems raised by customers.</Typography>
        </Box>
        <Tooltip title="Refresh"><span><IconButton onClick={load} disabled={loading} sx={{ background: '#f1f5f9', borderRadius: '10px' }}><MdRefresh size={20} /></IconButton></span></Tooltip>
      </Box>

      {notice && <Alert severity="success" onClose={() => setNotice('')} sx={{ mb: 2 }}>{notice}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
        <Kpi title="Open" value={stats.open} icon={FaRegCircle} from="#ef4444" to="#b91c1c" active={filters.status === 'open'} onClick={() => pickStatus('open')} />
        <Kpi title="In progress" value={stats.inProgress} icon={FaSpinner} from="#f59e0b" to="#b45309" active={filters.status === 'in-progress'} onClick={() => pickStatus('in-progress')} />
        <Kpi title="Resolved" value={stats.resolved} icon={FaRegCheckCircle} from="#22c55e" to="#15803d" active={filters.status === 'resolved'} onClick={() => pickStatus('resolved')} />
        <Kpi title="Unassigned" value={stats.unassigned} icon={MdPersonOff} from="#6366f1" to="#4338ca" active={filters.assigned === 'unassigned'} onClick={() => setF('assigned', filters.assigned === 'unassigned' ? '' : 'unassigned')} />
        <Kpi title="High priority (active)" value={stats.highPriority} icon={MdPriorityHigh} from="#e11d48" to="#9f1239" active={filters.priority === 'high'} onClick={() => setF('priority', filters.priority === 'high' ? '' : 'high')} />
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, p: 1.75, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px' }}>
        <TextField size="small" placeholder="Search subject, reference, customer…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 240 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch size={18} /></InputAdornment> }} />
        <TextField select size="small" label="Status" value={filters.status} onChange={(e) => setF('status', e.target.value)} sx={{ minWidth: 140 }}>
          <MenuItem value="">All</MenuItem><MenuItem value="active">Open + in progress</MenuItem><MenuItem value="open">Open</MenuItem><MenuItem value="in-progress">In progress</MenuItem><MenuItem value="resolved">Resolved</MenuItem>
        </TextField>
        <TextField select size="small" label="Priority" value={filters.priority} onChange={(e) => setF('priority', e.target.value)} sx={{ minWidth: 130 }}>
          <MenuItem value="">Any</MenuItem><MenuItem value="high">High</MenuItem><MenuItem value="normal">Normal</MenuItem><MenuItem value="low">Low</MenuItem>
        </TextField>
        <TextField select size="small" label="Assigned to" value={filters.assigned} onChange={(e) => setF('assigned', e.target.value)} sx={{ minWidth: 170 }}>
          <MenuItem value="">Anyone</MenuItem><MenuItem value="me">Me</MenuItem><MenuItem value="unassigned">Unassigned</MenuItem>
          {staff.map((s) => <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Order" value={filters.hasOrder} onChange={(e) => setF('hasOrder', e.target.value)} sx={{ minWidth: 150 }}>
          <MenuItem value="">Any</MenuItem><MenuItem value="yes">About an order</MenuItem><MenuItem value="no">General</MenuItem>
        </TextField>
        <TextField size="small" type="date" label="From" value={filters.from} onChange={(e) => setF('from', e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label="To" value={filters.to} onChange={(e) => setF('to', e.target.value)} InputLabelProps={{ shrink: true }} />
        {dirty && <Button onClick={() => { setSearch(''); setFilters({ ...EMPTY, status: 'open' }); setPage(1); }} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Reset</Button>}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={head}>Ticket</TableCell>
                <TableCell sx={head}>Customer</TableCell>
                <TableCell sx={head}>Order</TableCell>
                <TableCell sx={head}>Status</TableCell>
                <TableCell sx={head}>Priority</TableCell>
                <TableCell sx={head}>Assigned</TableCell>
                <TableCell sx={head}>Created</TableCell>
                <TableCell sx={head} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && [...Array(6)].map((_, i) => <TableRow key={i}>{[...Array(8)].map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>)}
              {!loading && rows.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6, fontFamily: FONT, color: '#64748b' }}>No tickets match these filters.</TableCell></TableRow>}
              {!loading && rows.map((q) => (
                <TableRow key={q._id} hover sx={{ cursor: 'pointer' }} onClick={() => { setActionError(''); setSelected(q); }}>
                  <TableCell sx={{ ...cell, maxWidth: 320 }}>
                    <Typography noWrap sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5 }} title={q.subject}>{q.subject || 'General inquiry'}</Typography>
                    <Typography noWrap sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{q.referenceNo} · {q.messageCount} repl{q.messageCount === 1 ? 'y' : 'ies'}</Typography>
                  </TableCell>
                  <TableCell sx={cell}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 28, height: 28, fontSize: 13, fontWeight: 800, bgcolor: '#e0e7ff', color: '#3730a3' }}>{initial(q.customerId?.name)}</Avatar>
                      <Box>
                        <div>{q.customerId?.name || 'Unknown'}</div>
                        <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{q.customerId?.mobile || q.customerId?.email || ''}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={cell}>{q.orderId?.orderNumber ? `#${q.orderId.orderNumber}` : <span style={{ color: '#94a3b8' }}>—</span>}</TableCell>
                  <TableCell><StatusChip status={q.status} /></TableCell>
                  <TableCell><PriorityChip priority={q.priority} /></TableCell>
                  <TableCell sx={cell}>{q.assigneeName || <span style={{ color: '#94a3b8' }}>Unassigned</span>}</TableCell>
                  <TableCell sx={cell}>{ago(q.createdAt)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Quick view"><IconButton size="small" onClick={() => { setActionError(''); setSelected(q); }} sx={{ color: '#4f46e5', background: '#4f46e514', borderRadius: '8px' }}><MdVisibility size={17} /></IconButton></Tooltip>{' '}
                    <Tooltip title="Open conversation"><IconButton size="small" onClick={() => navigate(`/queries/chat/${q._id}`)} sx={{ color: '#ca8a04', background: '#ca8a0414', borderRadius: '8px' }}><MdChat size={17} /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{meta.total} ticket{meta.total === 1 ? '' : 's'}</Typography>
          <Pagination count={meta.totalPages} page={page} onChange={(_, p) => setPage(p)} shape="rounded" size="small" />
        </Box>
      </Box>

      {/* quick view */}
      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="md" fullWidth scroll="paper" PaperProps={{ sx: { borderRadius: '18px', background: '#f8fafc' } }}>
        {selected && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2.25, background: 'linear-gradient(135deg,#6366f1,#4338ca)', color: '#fff' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 19 }}>{selected.subject || 'General inquiry'}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 13, opacity: 0.9 }}>{selected.referenceNo} · opened {dt(selected.createdAt)}</Typography>
              </Box>
              <StatusChip status={selected.status} />
              <IconButton onClick={() => setSelected(null)} size="small" sx={{ color: '#fff' }}><MdClose size={20} /></IconButton>
            </Box>
            <DialogContent sx={{ p: 3, display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' }, alignItems: 'start' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Card title="Customer’s message" accent="#f59e0b">
                  <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#0f172a', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{selected.description || 'No description given.'}</Typography>
                  {(selected.images || []).length > 0 && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
                      {selected.images.map((img, i) => <img key={i} src={img} alt="" onClick={() => window.open(img, '_blank')} style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0', cursor: 'pointer' }} />)}
                    </Box>
                  )}
                </Card>
                <Card title="Customer" accent="#0ea5e9">
                  <Row label="Name" value={selected.customerId?.name} />
                  <Row label="Mobile" value={selected.customerId?.mobile} />
                  <Row label="Email" value={selected.customerId?.email} />
                  <Row label="Order" value={selected.orderId?.orderNumber ? `#${selected.orderId.orderNumber} · ${selected.orderId.orderStatus || ''}` : null} />
                </Card>
              </Box>
              <Card title="Manage" accent="#22c55e">
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                  <TextField select size="small" label="Status" value={selected.status} disabled={saving} onChange={(e) => change({ status: e.target.value })}>
                    <MenuItem value="open">Open</MenuItem><MenuItem value="in-progress">In progress</MenuItem><MenuItem value="resolved">Resolved</MenuItem>
                  </TextField>
                  <TextField select size="small" label="Priority" value={selected.priority || 'normal'} disabled={saving} onChange={(e) => change({ priority: e.target.value })}>
                    <MenuItem value="high">High</MenuItem><MenuItem value="normal">Normal</MenuItem><MenuItem value="low">Low</MenuItem>
                  </TextField>
                  <TextField select size="small" label="Assigned to" value={selected.assignedTo?._id || selected.assignedTo || ''} disabled={saving} onChange={(e) => change({ assignedTo: e.target.value || null })}>
                    <MenuItem value="">Unassigned</MenuItem>
                    {staff.map((s) => <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>)}
                  </TextField>
                  <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>{selected.messageCount || 0} repl{selected.messageCount === 1 ? 'y' : 'ies'}{selected.lastReplyAt ? ` · last ${ago(selected.lastReplyAt)}` : ''}</Typography>
                  {actionError && <Alert severity="error">{actionError}</Alert>}
                </Box>
              </Card>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
              <Button onClick={() => setSelected(null)} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Close</Button>
              <Button startIcon={<MdChat />} variant="contained" onClick={() => navigate(`/queries/chat/${selected._id}`)} sx={yellowBtn}>Open conversation</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Queries;
